/**
 * src/utils/flagArrangements.ts
 *
 * The ways a flag can be worn by a building, and what a flag is made of.
 *
 * A tower is painted with `fill-extrusion`, which gives one solid colour
 * per slab, so a flag reaches a building as an ordered list of horizontal
 * bands and nothing else. There is more than one defensible way to get
 * from a flag to that list, and which one looks right is a matter of
 * judgement rather than correctness:
 *
 *   - The Burj Khalifa on National Day is red at the spire, then white,
 *     then green at the base — the UAE's hoist colour crowned and its
 *     stripes climbing in reverse. Nothing about the flag says to do
 *     that; it is how the building is actually lit.
 *   - The Stars and Stripes has no arrangement that is faithful, because
 *     its canton sits beside its stripes rather than above them. Blue on
 *     top is a reading, not a reproduction.
 *   - Thirteen stripes on a tower are stripes nobody can count. The same
 *     flag in three colours is more legible and less true.
 *
 * So this module generates every arrangement it can defend and leaves the
 * choice to the operator, who can see the building. Each is pure data
 * derived from one `FlagReading`, so a preview and the paint that follows
 * cannot disagree.
 *
 * `FlagReading` lives here rather than beside the pixel reader so that
 * `countryFlagPalette` can depend on this module and not the reverse:
 * reading a bitmap and deciding how to wear the result are separate jobs,
 * and only one of them needs a canvas.
 */

/** What was found in a flag bitmap, before any decision about wearing it. */
export interface FlagReading {
  /**
   * The stripes as the flag reads them: top to bottom for a horizontal
   * flag, hoist to fly for a vertical one. Excludes any hoist bar or
   * canton, which cross the stripes rather than joining them.
   */
  stripes: string[];
  orientation: 'horizontal' | 'vertical';
  /** A bar spanning a whole edge: the UAE's red, Kuwait's black. */
  hoist: string | null;
  /** A block in the upper hoist corner that stops short: the US union. */
  canton: string | null;
  /** Distinct colours ranked by how much of the flag they cover. */
  dominant: string[];
}

export type FlagArrangementId =
  | 'hoist_crown'
  | 'accent_top'
  | 'accent_top_simple'
  | 'flag_order'
  | 'flag_order_simple'
  | 'flag_reversed'
  | 'dominant_three'
  | 'dominant_all'
  | 'crown_and_columns'
  | 'columns_only';

/**
 * How the colours meet the building.
 *
 * Horizontal bands need nothing but a colour list, because a slab is
 * already a horizontal band. Columns need the footprint cut into strips,
 * which is real geometry and is why the two are not interchangeable.
 */
export type FlagLayout =
  | { kind: 'bands'; bands: string[] }
  | {
    kind: 'columns';
    /** Hoist to fly across the footprint. */
    columns: string[];
    /** A colour capping the tower above the columns, if any. */
    crown: string | null;
    /** Share of the building's height the crown takes, 0 to 0.9. */
    crownShare: number;
  };

export interface FlagArrangement {
  id: FlagArrangementId;
  /** Plain English, and specific about which colour goes where. */
  label: string;
  /** One line on why an operator would choose this one. */
  note: string;
  /**
   * The colours from the top of the building down.
   *
   * Always populated, including for a columned layout, where it is the
   * closest horizontal reading of the same flag: a legacy painter, a
   * preview swatch or a building with no usable footprint still has
   * something to show.
   */
  bands: string[];
  layout: FlagLayout;
}

/** Colours as horizontal bands, which is what most arrangements are. */
function asBands(bands: string[]): FlagLayout {
  return { kind: 'bands', bands };
}

const HEX_COLOUR = /^#[0-9a-fA-F]{3,8}$/;

/** Colour strings only, since these end up in a paint property. */
function coloursIn(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((c): c is string => typeof c === 'string' && HEX_COLOUR.test(c));
}

/**
 * A layout read back from storage, or null if there is nothing usable.
 *
 * A layout makes the round trip through object metadata and the local
 * intent cache, so it arrives as whatever JSON happened to be written —
 * possibly by an older or newer build. Anything unrecognised is rejected
 * rather than patched up, because the caller has a colour list to fall
 * back on and a half-understood layout would paint a building nobody
 * approved.
 */
export function parseFlagLayout(value: unknown): FlagLayout | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as { kind?: unknown; bands?: unknown; columns?: unknown; crown?: unknown; crownShare?: unknown };

  if (raw.kind === 'bands') {
    const bands = coloursIn(raw.bands);
    return bands.length > 0 ? { kind: 'bands', bands } : null;
  }

  if (raw.kind === 'columns') {
    const columns = coloursIn(raw.columns);
    if (columns.length === 0) return null;
    const crown = typeof raw.crown === 'string' && HEX_COLOUR.test(raw.crown) ? raw.crown : null;
    const share = Number(raw.crownShare);
    return {
      kind: 'columns',
      columns,
      crown,
      crownShare: crown && Number.isFinite(share) && share > 0
        ? Math.min(0.9, share)
        : (crown ? 1 / (columns.length + 1) : 0),
    };
  }

  return null;
}

/**
 * Two bands is the least that reads as a flag rather than as a building
 * that happens to be painted, so single-band candidates are held back —
 * but only while something better exists. A genuinely monochrome flag has
 * one colour and no amount of preference produces a second.
 */
const MIN_BANDS = 2;

/** More than this and the operator is choosing between near-identical towers. */
const MAX_ARRANGEMENTS = 9;

/** What makes two arrangements the same building. */
function signatureOf(a: FlagArrangement): string {
  const colours = a.layout.kind === 'columns'
    ? [a.layout.crown ?? '-', ...a.layout.columns]
    : a.layout.bands;
  return `${a.layout.kind}:${colours.map((c) => c.toLowerCase()).join('|')}`;
}

/** Colours in first-appearance order, repeats dropped. */
function uniqueInOrder(colours: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of colours) {
    const key = c.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

/**
 * Every arrangement worth offering for one flag, best guess first.
 *
 * The leading entry is what gets used when nobody chooses: a hoist means
 * the hoist crowns the tower, a canton means the canton does, and a plain
 * striped flag reads straight down. Empty when the flag could not be read
 * well enough to paint anything — inventing colours for a national flag
 * is worse than drawing none.
 */
export function flagArrangements(reading: FlagReading): FlagArrangement[] {
  const stripes = (reading.stripes ?? []).filter((c) => typeof c === 'string' && c);
  const dominant = (reading.dominant ?? []).filter((c) => typeof c === 'string' && c);
  const hoist = reading.hoist || null;
  const canton = reading.canton || null;

  /** The one colour that crosses the stripes, whichever kind it is. */
  const accent = canton || hoist;
  const accentWord = canton ? 'canton' : 'hoist';

  const candidates: FlagArrangement[] = [];

  const columnsOf = (colours: string[], crown: string | null): FlagArrangement['layout'] => ({
    kind: 'columns',
    columns: colours,
    crown,
    // The crown takes the share of the height a hoist bar takes of a
    // flag's length when the bar and the stripes divide it evenly: a
    // quarter for three stripes, which is the UAE's own proportion.
    crownShare: crown ? 1 / (colours.length + 1) : 0,
  });

  if (accent && stripes.length > 0) {
    // The flag stood upright against the tower. This is what a flag hung
    // on a building actually looks like — the UAE's red at the top with
    // green, white and black running down beside each other — and it needs
    // the footprint cut into strips, not just a colour list.
    const upright: FlagArrangement = {
      id: 'crown_and_columns',
      label: `${cap(accentWord)} on top, stripes as columns`,
      note: 'The flag stood upright on the tower: the crossing colour caps it and the stripes run down the facade side by side.',
      bands: [accent, ...stripes],
      layout: columnsOf(stripes, accent),
    };
    const crown: FlagArrangement = {
      id: 'hoist_crown',
      label: `${cap(accentWord)} on top, bands reversed`,
      note: 'Horizontal bands only: the crossing colour crowns the tower and the stripes climb back up it.',
      bands: [accent, ...stripes.slice().reverse()],
      layout: asBands([accent, ...stripes.slice().reverse()]),
    };
    const inOrder: FlagArrangement = {
      id: 'accent_top',
      label: `${cap(accentWord)} on top, bands in flag order`,
      note: 'Horizontal bands only: the crossing colour takes the crown and the stripes read downwards as they do on the flag.',
      bands: [accent, ...stripes],
      layout: asBands([accent, ...stripes]),
    };

    // A hoist bar runs the height of the flag, so a tower wearing one is
    // reading the flag rotated: upright columns first, then the horizontal
    // reading with the stripes coming back up. A canton sits above only
    // its first few stripes, so the honest reading keeps them running
    // downwards from it and the rotation is offered second.
    candidates.push(...(canton && !hoist
      ? [inOrder, upright, crown]
      : [upright, crown, inOrder]));
    candidates.push({
      id: 'accent_top_simple',
      label: `${cap(accentWord)} on top, one band per colour`,
      note: 'Repeated stripes collapsed, so a flag with many of them reads at street level instead of blurring.',
      bands: uniqueInOrder([accent, ...stripes]),
      layout: asBands(uniqueInOrder([accent, ...stripes])),
    });
  }

  if (stripes.length > 0) {
    candidates.push({
      id: 'flag_order',
      label: 'Bands only, in flag order',
      note: 'The flag read straight down the building, ignoring anything that crosses the stripes.',
      bands: stripes,
      layout: asBands(stripes),
    });
    candidates.push({
      id: 'flag_order_simple',
      label: 'Bands only, one per colour',
      note: 'As above with repeats collapsed.',
      bands: uniqueInOrder(stripes),
      layout: asBands(uniqueInOrder(stripes)),
    });
    candidates.push({
      id: 'flag_reversed',
      label: 'Bands only, reversed',
      note: 'The flag upside down, which some towers wear better: the last stripe crowns the building.',
      bands: stripes.slice().reverse(),
      layout: asBands(stripes.slice().reverse()),
    });
  }

  // After the horizontal readings, because a flag with nothing crossing it
  // has an obvious right way up and turning it on its side is a choice
  // rather than the answer. A hoist flag is the other way round: it is
  // already offered upright at the top of the list.
  if (stripes.length >= 2) {
    candidates.push({
      id: 'columns_only',
      label: 'Stripes as columns',
      note: 'The stripes turned upright, running down the facade side by side with nothing capping the tower.',
      bands: stripes,
      layout: columnsOf(stripes, null),
    });
  }

  if (dominant.length > 0) {
    candidates.push({
      id: 'dominant_three',
      label: 'Three main colours',
      note: 'The three colours covering most of the flag, largest at the top. Nothing about the layout, just the palette.',
      bands: dominant.slice(0, 3),
      layout: asBands(dominant.slice(0, 3)),
    });
    candidates.push({
      id: 'dominant_all',
      label: 'Every main colour',
      note: 'For a flag that is not striped at all — a cross or a crest — where any stripe reading would be invented.',
      bands: dominant,
      layout: asBands(dominant),
    });
  }

  // A striped reading beats a palette one, so the ranking above is kept;
  // the first survivor becomes the default. Two arrangements that paint the
  // same building are the same arrangement, and offering both as a choice
  // would be a choice about nothing — but the same colours laid out
  // differently are two different buildings, so the layout is part of what
  // makes an arrangement distinct.
  const survivors = (minBands: number): FlagArrangement[] => {
    const seen = new Set<string>();
    const out: FlagArrangement[] = [];
    for (const c of candidates) {
      if (c.bands.length < minBands) continue;
      if (seen.has(signatureOf(c))) continue;
      seen.add(signatureOf(c));
      out.push(c);
      if (out.length >= MAX_ARRANGEMENTS) break;
    }
    return out;
  };

  const banded = survivors(MIN_BANDS);
  return banded.length > 0 ? banded : survivors(1);
}

/** The arrangement used when nobody has chosen one, or null if unreadable. */
export function defaultArrangement(reading: FlagReading): FlagArrangement | null {
  return flagArrangements(reading)[0] ?? null;
}

/**
 * A named arrangement, falling back to the default.
 *
 * A stored id can outlive the reading that produced it — a flag bitmap
 * changes, or an arrangement is retired — and a building that has been
 * told to wear a flag should still wear one.
 */
export function arrangementById(
  reading: FlagReading,
  id: string | null | undefined,
): FlagArrangement | null {
  const all = flagArrangements(reading);
  const chosen = id ? all.find((a) => a.id === id) : undefined;
  return chosen ?? all[0] ?? null;
}

/** The bands for a named arrangement, falling back to the default. */
export function bandsForArrangement(
  reading: FlagReading,
  id: string | null | undefined,
): string[] {
  return arrangementById(reading, id)?.bands ?? [];
}

function cap(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
